import React, { useEffect, useState } from "react";

import Input from "components/Input/index";
import Select from "components/Select/index";
import RadioLabelComponent from "components/Toggles/RadioLabelComponent";
import Checkbox from "components/Checkbox/index";
import Textarea from "components/Input/Textarea";

import { approvalOptions, CREATOR_TYPES, selectOptionsMap } from "../data";
import { DetailsWrapperStyles } from "../styled";
import {
  isValidMinMax,
  validateBudgetRange,
  validateMinMaxRange,
  validateSingleBudget
} from "utils/validateMinMax";
import { HelperText } from "components/Input/styled";
import MultiSelectSearch from "components/MultiSelectSearch/index";

const RequestDetails = ({
  value = {},
  onChange,
  dropdownOptions,
  setHasRequestErrors,
  categories,
  languages,
  companies,
  cities,
  onCategorySearch,
  categoriesLoading,
  onLanguageSearch,
  languageLoading,
  onCompanySearch,
  onCitySearch,
  citiesLoading
}) => {
  const updateField = (key, val) => {
    onChange((prev) => ({ ...prev, [key]: val }));
  };

  const [creatorCountErrors, setCreatorCountErrors] = useState({
    min: "",
    max: ""
  });
  const [budgetErrors, setBudgetErrors] = useState({
    min: "",
    max: ""
  });
  const [budgetError, setBudgetError] = useState("");
  const [budgetTouched, setBudgetTouched] = useState(false);

  // Dropdown options
  const categoryOptions = categories?.map((cat) => ({
    value: cat.id,
    label: cat.name
  }));

  const languageOptions = languages?.map((cat) => ({
    value: cat.id,
    label: cat.name
  }));

  const cityOptions = cities?.map((cat) => ({
    value: cat.id,
    label: cat.name
  }));

  const isBudgetRange = value.is_budget_range;

  const serviceType = value?.service_type;
  const isInfluencer =
    serviceType === "influencer_marketing_agencies" ||
    serviceType === "influencers" ||
    serviceType === "talent_management_agencies";
  const hasServiceType = !!serviceType;

  useEffect(() => {
    if (value.budget_type) {
      setBudgetTouched(false);
    }
  }, [value.budget_type]);

  useEffect(() => {
    let hasErrors = false;

    /* ---------- Creator Count Validation ---------- */
    const creatorCountTouched =
      !value.barter &&
      ((value.creator_count_min !== null && value.creator_count_min !== "") ||
        (value.creator_count_max !== null && value.creator_count_max !== ""));

    if (creatorCountTouched) {
      if (creatorCountErrors.min || creatorCountErrors.max) {
        hasErrors = true;
      }
    }

    /* ---------- Budget Validation ---------- */
    if (value.is_budget_range) {
      const budgetRangeTouched =
        value.budget_min !== null || value.budget_max !== null;

      if (budgetRangeTouched) {
        if (budgetErrors.min || budgetErrors.max) {
          hasErrors = true;
        }
      }
    } else {
      const singleBudgetTouched = value.budget !== null && value.budget !== "";

      if (singleBudgetTouched && budgetError) {
        hasErrors = true;
      }
    }

    setHasRequestErrors(hasErrors);
  }, [value, creatorCountErrors, budgetErrors, budgetError]);

  const handleBudgetChange = (field, val) => {
    const nextValues = {
      budget_min: field === "budget_min" ? val : value.budget_min,
      budget_max: field === "budget_max" ? val : value.budget_max
    };

    updateField(field, val);

    const errors = validateBudgetRange(
      nextValues.budget_min,
      nextValues.budget_max
    );

    setBudgetErrors(errors);
  };

  const toggleBudgetRange = (val) => {
    updateField("is_budget_range", val);
    setBudgetErrors({ min: "", max: "" });

    if (val) {
      updateField("budget", null);
    } else {
      updateField("budget_min", null);
      updateField("budget_max", null);
    }
  };

  const handleSingleBudgetChange = (val) => {
    updateField("budget", val);
    setBudgetError(validateSingleBudget(val));
  };

  const handleCreatorCountChange = (field, val) => {
    const nextValues = {
      creator_count_min:
        field === "creator_count_min" ? val : value.creator_count_min,
      creator_count_max:
        field === "creator_count_max" ? val : value.creator_count_max
    };

    updateField(field, val);

    const errors = validateMinMaxRange(
      nextValues.creator_count_min,
      nextValues.creator_count_max
    );

    setCreatorCountErrors(errors);
  };

  const creatorCountCommonError =
    creatorCountErrors.max || creatorCountErrors.min;

  const budgetCommonErrors = budgetErrors.max || budgetErrors.min;

  return (
    <DetailsWrapperStyles>
      {/* STEP 1: Always show Service Type */}
      {!hasServiceType && (
        <div className="left-side-wrapper">
          <Select
            label="Service Type"
            required
            options={selectOptionsMap?.service_type}
            value={value?.service_type || ""}
            onChange={(val) => updateField("service_type", val)}
          />
        </div>
      )}

      {/* STEP 2: If Influencer Marketing Agencies → SHOW FULL FORM */}
      {hasServiceType && isInfluencer && (
        <>
          <div className="left-side-wrapper">
            <Select
              label="Service Type"
              required
              options={selectOptionsMap?.service_type}
              value={value?.service_type || ""}
              onChange={(val) => updateField("service_type", val)}
            />

            <Input
              id="target_audience"
              label="Target Audience"
              value={value?.target_audience || ""}
              onChange={(e) => updateField("target_audience", e.target.value)}
              disabled
              required
            />

            <div className="platform">
              <h4>Platform</h4>
              <RadioLabelComponent
                labelHeader="Instagram"
                checked={value.platform === "Instagram"}
                onChange={() => updateField("platform", "Instagram")}
              />

              <RadioLabelComponent
                labelHeader="YouTube"
                checked={value.platform === "YouTube"}
                onChange={() => updateField("platform", "YouTube")}
              />

              <RadioLabelComponent
                labelHeader="Both"
                checked={value.platform === "Both"}
                onChange={() => updateField("platform", "Both")}
              />
            </div>

            {/* <Select
              id="categories_selector"
              label="Categories"
              options={categoryOptions}
              value={value.categories_selector || []} // make sure it's always an array
              onChange={(val) => {
                // val is an array of selected options
                updateField("categories_selector", val || []);
              }}
              isMulti={true}
              maxSelect={5}
            /> */}
            <MultiSelectSearch
              id="categories_selector"
              label="Categories (Upto 5)"
              options={categoryOptions}
              value={value.categories_selector || []}
              onChange={(val) => updateField("categories_selector", val)}
              placeholder="Select Value"
              maxSelect={5}
              onSearch={onCategorySearch}
              loading={categoriesLoading}
            />

            <div className="toggle-wrapper">
              {/* <Select
                id="city_selector"
                label="City"
                options={cityOptions}
                value={value.city_selector || []}
                onChange={(val) => updateField("city_selector", val || [])}
                isMulti={true}
                maxSelect={5}
                disabled={value.is_pan_india}
              /> */}

              <MultiSelectSearch
                id="city_selector"
                label="Cities (Upto 5)"
                options={cityOptions}
                value={value.city_selector || []}
                onChange={(val) => updateField("city_selector", val)}
                placeholder="Select Value"
                maxSelect={5}
                onSearch={onCitySearch}
                loading={citiesLoading}
                disabled={value.is_pan_india}
              />

              <Checkbox
                checked={value.is_pan_india}
                size="M"
                label="Pan India"
                color="neutral"
                onChange={(val) => {
                  updateField("is_pan_india", val);

                  if (val) {
                    updateField("city_selector", []);
                  }
                }}
              />
            </div>

            <div className="toggle-wrapper">
              {/* <Select
                id="language_selector"
                label="Languages"
                options={languageOptions}
                value={value.language_selector || []}
                onChange={(val) => updateField("language_selector", val || [])}
                isMulti={true}
                maxSelect={5}
                disabled={value.any_language}
              /> */}

              <MultiSelectSearch
                id="language_selector"
                label="Languages (Upto 5)"
                options={languageOptions}
                value={value.language_selector || []}
                onChange={(val) => updateField("language_selector", val)}
                placeholder="Select Value"
                maxSelect={5}
                onSearch={onLanguageSearch}
                loading={languageLoading}
                disabled={value.is_pan_india}
              />

              <Checkbox
                checked={value.any_language}
                size="M"
                label="Any Language"
                color="neutral"
                onChange={(val) => {
                  updateField("any_language", val);

                  if (val) {
                    updateField("language_selector", []);
                  }
                }}
              />
            </div>

            <div className="platform">
              <h4>High Deal</h4>
              <div className="radio-wrapper">
                <RadioLabelComponent
                  labelHeader="Yes"
                  checked={value.is_high_value === true}
                  onChange={() => updateField("is_high_value", true)}
                />
                <RadioLabelComponent
                  labelHeader="No"
                  checked={value.is_high_value === false}
                  onChange={() => updateField("is_high_value", false)}
                />
              </div>
            </div>

            <Textarea
              id="description"
              label="Description"
              placeholder="Add description"
              rows={4}
              value={value.description || ""}
              maxLength={512} // limit
              onChange={(e) => {
                const val = e.target.value;
                if (val.length <= 512) {
                  updateField("description", val);
                }
              }}
              required
            />
          </div>

          <div className="right-side-wrapper">
            <div className="multiple-fields">
              <Select
                id="brand type"
                label="Brand Type"
                options={selectOptionsMap?.brand_info}
                value={value.brand_info || ""}
                onChange={(val) => updateField("brand_info", val)}
              />

              <Input
                id="brand_name"
                placeholder="Brand Name"
                value={value.brand || ""}
                onChange={(e) => updateField("brand", e.target.value)}
              />
            </div>
            <div className="common-fields">
              <div className="multiple-fields">
                <Input
                  id="creator_count_min"
                  label="Required Creators"
                  placeholder="Min value"
                  type="number"
                  value={value.creator_count_min ?? ""}
                  hasError={!!creatorCountErrors.min}
                  onChange={(e) =>
                    handleCreatorCountChange(
                      "creator_count_min",
                      e.target.value
                    )
                  }
                />

                <Input
                  id="creator_count_max"
                  placeholder="Max value"
                  type="number"
                  value={value.creator_count_max ?? ""}
                  hasError={!!creatorCountErrors.max}
                  onChange={(e) =>
                    handleCreatorCountChange(
                      "creator_count_max",
                      e.target.value
                    )
                  }
                />
              </div>

              {creatorCountCommonError && (
                <HelperText hasError={!!creatorCountErrors.max} size="M">
                  {creatorCountCommonError}
                </HelperText>
              )}

              <Checkbox
                checked={value.barter}
                size="M"
                label="Barter"
                color="neutral"
                onChange={(val) => {
                  updateField("barter", val);

                  if (val) {
                    updateField("budget", null);
                    updateField("budget_min", null);
                    updateField("budget_max", null);
                    updateField("budget_type", null);
                    setBudgetErrors({ min: "", max: "" });
                  }
                }}
              />
            </div>

            {!value?.barter ? (
              <>
                <Select
                  id="budget_type"
                  label="Budget Type"
                  options={selectOptionsMap?.budget_type}
                  value={value.budget_type || ""}
                  onChange={(val) => updateField("budget_type", val)}
                />

                <div className="common-fields">
                  {!isBudgetRange ? (
                    <Input
                      id="budget"
                      label="Budget"
                      placeholder="Enter a budget"
                      leftIcon="₹"
                      type="number"
                      min={1000}
                      value={value.budget || ""}
                      helperText={budgetError}
                      hasError={
                        (budgetTouched && !value.budget_type) || !!budgetError
                      }
                      onFocus={() => setBudgetTouched(true)}
                      disabled={budgetTouched && !value.budget_type}
                      onChange={(e) => handleSingleBudgetChange(e.target.value)}
                    />
                  ) : (
                    <div className="multiple-fields">
                      <Input
                        id="budget_min"
                        label="Budget"
                        leftIcon="₹"
                        type="number"
                        min={1000}
                        placeholder="Enter min Budget"
                        value={value.budget_min || ""}
                        hasError={
                          (budgetTouched && !value.budget_type) ||
                          !!budgetErrors.min
                        }
                        onFocus={() => setBudgetTouched(true)}
                        disabled={budgetTouched && !value.budget_type}
                        onChange={(e) =>
                          handleBudgetChange("budget_min", e.target.value)
                        }
                      />

                      <Input
                        id="budget_max"
                        leftIcon="₹"
                        type="number"
                        placeholder="Enter max Budget"
                        value={value.budget_max || ""}
                        hasError={
                          (budgetTouched && !value.budget_type) ||
                          !!budgetErrors.max
                        }
                        onFocus={() => setBudgetTouched(true)}
                        disabled={budgetTouched && !value.budget_type}
                        onChange={(e) =>
                          handleBudgetChange("budget_max", e.target.value)
                        }
                      />
                    </div>
                  )}

                  {budgetTouched && !value.budget_type && (
                    <HelperText hasError size="M">
                      Choose the budget type
                    </HelperText>
                  )}

                  {budgetCommonErrors && (
                    <HelperText hasError={budgetCommonErrors} size="M">
                      {budgetCommonErrors}
                    </HelperText>
                  )}

                  <Checkbox
                    label="Add as Range"
                    checked={value.is_budget_range}
                    onChange={toggleBudgetRange}
                  />
                </div>
              </>
            ) : null}

            <div className="platform">
              <h4>
                Creator Type{" "}
                {!value.platform ? "(Choose any one platform)" : ""}
              </h4>

              {CREATOR_TYPES?.map((type) => (
                <Checkbox
                  key={type}
                  label={type}
                  checked={(value.creator_range_selector || []).includes(type)}
                  disabled={!value.platform}
                  onChange={(checked) => {
                    updateField(
                      "creator_range_selector",
                      checked
                        ? [...(value.creator_range_selector || []), type] // ADD
                        : (value.creator_range_selector || []).filter(
                            (t) => t !== type
                          ) // REMOVE
                    );
                  }}
                />
              ))}
            </div>
            <Select
              id="status"
              label="Approval Status"
              required
              options={approvalOptions}
              value={value.status || ""}
              onChange={(val) => updateField("status", val)}
              disabled
            />
          </div>
        </>
      )}

      {/* STEP 3: Other Service Types → ONLY Description + Status */}
      {hasServiceType && !isInfluencer && (
        <div className="left-side-wrapper">
          <Select
            label="Service Type"
            required
            options={selectOptionsMap?.service_type}
            value={value?.service_type || ""}
            onChange={(val) => updateField("service_type", val)}
          />

          <Textarea
            id="description"
            label="Description"
            placeholder="Add description"
            rows={4}
            value={value.description || ""}
            maxLength={512} // limit
            onChange={(e) => {
              const val = e.target.value;
              if (val.length <= 512) {
                updateField("description", val);
              }
            }}
            required
          />

          <Select
            id="status"
            label="Approval Status"
            required
            options={approvalOptions}
            value={value.status || ""}
            onChange={(val) => updateField("status", val)}
            disabled
          />
        </div>
      )}
    </DetailsWrapperStyles>
  );
};

export default RequestDetails;
